import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Appearance, useColorScheme } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { themes, darkTheme } from "../config/themes";

const STORAGE_KEY = "@maritime_theme_mode_v1";

// `mode` is what the user chose ("light" | "dark" | "system"). `resolved` is
// the actual theme key being rendered (always "light" or "dark"). When mode
// is "system" we follow the device's color scheme; otherwise we use mode.
const ThemeContext = createContext({
  theme: darkTheme,
  mode: "dark",
  resolved: "dark",
  setMode: () => {},
  toggleMode: () => {},
});

export function ThemeProvider({ children }) {
  const systemScheme = useColorScheme(); // "light" | "dark" | null
  const [mode, setModeState] = useState("dark");
  const [hydrated, setHydrated] = useState(false);

  // Hydrate the saved mode from AsyncStorage on first render.
  useEffect(() => {
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        if (stored === "light" || stored === "dark" || stored === "system") {
          setModeState(stored);
        }
      } catch {
        // Best-effort — fall back to default.
      } finally {
        setHydrated(true);
      }
    })();
  }, []);

  const setMode = async (next) => {
    setModeState(next);
    try {
      await AsyncStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  // Resolve "system" to whatever the OS color scheme is right now (defaults
  // to dark if the platform can't report one).
  const resolved =
    mode === "system" ? (systemScheme || Appearance.getColorScheme() || "dark") : mode;

  const toggleMode = () => setMode(resolved === "dark" ? "light" : "dark");

  const value = useMemo(
    () => ({
      mode,
      resolved,
      theme: themes[resolved] || darkTheme,
      setMode,
      toggleMode,
    }),
    [mode, resolved],
  );

  // Don't render until we've attempted to hydrate — prevents a flash.
  if (!hydrated) return null;

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Access the current theme + controls. */
export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
